import * as React from 'react';
import { useMemo } from 'react';
import { EnvelopeIcon, PhoneIcon, ClockIcon } from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';

import { cn, formatDateToDayMonthYear } from '@restworld/utils-common';
import { Dropdown, DropdownItem, Badge, Heading, IconButton, Link } from '@restworld/ui-ds-base';

export function EmployerCollaboratorCard({
  email,
  firstName,
  lastName,
  lastEdit,
  phoneNumber,
  isAdmin,
  isInvited,
  onEditCollaborator,
  onRemoveCollaborator,
  areActionsDisabled,
}: EmployerCollaboratorCardProps) {
  const { t } = useTranslation('collaborators-page');

  const menuItemList: DropdownItem[] = useMemo(
    () => [
      {
        label: t('edit_permission_level'),
        onClick: onEditCollaborator,
        Icon: PencilSquareIcon,
      },
      {
        label: t('remove_collaborator'),
        onClick: onRemoveCollaborator,
        Icon: TrashIcon,
        isCriticalAction: true,
      },
    ],
    [onEditCollaborator, onRemoveCollaborator, t]
  );

  return (
    <MainWrapper>
      <LeftColumnWrapper>
        <Heading size={5} weight="semibold" color="neutral">
          {`${firstName || ''} ${lastName || ''}`}
        </Heading>
        <EmailPhoneLastEditWrapper>
          <StyledLink link={`mailto:${email}`}>
            <EnvelopeIcon className="h-4 w-4" />
            <span>{email}</span>
          </StyledLink>
          <StyledLink link={`tel:${phoneNumber}`}>
            <PhoneIcon className="h-4 w-4" />
            <span>{phoneNumber}</span>
          </StyledLink>
          <LastEditDateDisplay lastEdit={lastEdit} />
        </EmailPhoneLastEditWrapper>
        <LastEditDateDisplay lastEdit={lastEdit} forTablet />
      </LeftColumnWrapper>
      <RightColumnWrapper>
        <BadgesWrapper>
          {isInvited && (
            <Badge variant="warning" className="w-fit">
              {t('invited')}
            </Badge>
          )}
          <Badge variant={isAdmin ? 'restworld' : 'disabled'}>{t(isAdmin ? 'admin' : 'base')}</Badge>
        </BadgesWrapper>
        <Dropdown
          items={menuItemList}
          triggerElement={
            <IconButton
              ariaLabel="view options"
              Icon={EllipsisVerticalIcon}
              className="text-neutral-700"
              size="sm"
              disabled={areActionsDisabled}
            />
          }
          disabled={areActionsDisabled}
        />
      </RightColumnWrapper>
    </MainWrapper>
  );
}

export default EmployerCollaboratorCard;

export interface EmployerCollaboratorCardProps {
  email: string;
  phoneNumber: string;
  lastEdit: string;
  firstName: string;
  lastName: string;
  isAdmin?: boolean;
  isInvited?: boolean;
  onEditCollaborator?: () => void;
  onRemoveCollaborator?: () => void;
  areActionsDisabled?: boolean;
}

const LastEditDateDisplay = ({ lastEdit, forTablet }: { lastEdit: string; forTablet?: boolean }) => {
  const { t } = useTranslation('collaborators-page');

  return (
    <LastEditWrapper forTablet={forTablet}>
      <ClockIcon className="h-4 w-4" />
      <span>
        {t('last_edit')}: {formatDateToDayMonthYear(lastEdit)}
      </span>
    </LastEditWrapper>
  );
};

const MainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'bg-neutral-10 border border-neutral-40 rounded-2xl shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)] p-4 flex flex-col-reverse items-center justify-between md:flex-row md:px-6 md:py-5 md:gap-2',
      className
    )}
    {...props}
  />
);
const StyledLink = ({ className, ...props }: any) => (
  <Link
    className={cn(
      'text-sm leading-normal font-medium text-neutral-200 flex items-center gap-1 md:justify-center',
      className
    )}
    {...props}
  />
);
const LeftColumnWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full md:w-fit', className)} {...props} />
);
const RightColumnWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex items-center justify-between gap-3 w-full mb-2 md:justify-start md:w-fit md:min-w-fit md:mb-0',
      className
    )}
    {...props}
  />
);
const EmailPhoneLastEditWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-1 mt-1 md:flex-row md:gap-5', className)} {...props} />
);
const LastEditWrapper = ({ forTablet, className, ...props }: any) => (
  <span
    className={cn(
      'text-sm leading-normal font-medium text-neutral-200 items-center gap-1 lg:justify-center',
      forTablet ? 'hidden md:flex lg:hidden' : 'flex md:hidden lg:flex',
      className
    )}
    {...props}
  />
);
const BadgesWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-3 items-center', className)} {...props} />
);
